import { useValidatedCubeQuery } from "app/hooks/cube";
import { createCombinedError } from "app/hooks/cube/utils";
import { type FilterBarCubeFilters } from "app/views/dashboards/utils/costDashboardFilterBar";

import { hasAccountFilters } from "./filterTransformUtils";
import {
  costSavingOpportunityResultArraySchema,
  type CostSavingOpportunityResult,
} from "./useCostSavingOpportunity.schema";

import type { Query, Filter, QueryOrder } from "@cubejs-client/core";
import type { Range } from "types/cube";

const CUBE_NAME = "CSO" as const;

/** Props for the useCostSavingOpportunity hook */
interface UseCostSavingOpportunityProps {
  /** Dashboard key to filter by */
  dashboardKey: string;
  /** Date range for the query */
  dateRange: Range;
  /** Optional section key to filter by */
  sectionKey?: string;
  /** Optional additional filters to apply */
  filters?: FilterBarCubeFilters;
  /** Whether to include dashboard summary segment for aggregated costs */
  dashboardSummary?: boolean;
  /** Whether to include section_key as a dimension for grouping */
  includeSectionDimension?: boolean;
  /** Optional custom order for the query results */
  order?: Record<string, QueryOrder>;
  /** Whether the query is enabled */
  enabled?: boolean;
}

/** Props for the useCostSavingOpportunity hook result */
interface UseCostSavingOpportunityResult {
  /** Validated cost saving opportunity data */
  data: CostSavingOpportunityResult[] | null;
  /** Whether the query is loading */
  isLoading: boolean;
  /** Combined error from query and validation */
  error: Error | null;
}

function buildQuery(
  dashboardKey: string,
  dateRange: Range,
  sectionKey?: string,
  filters?: FilterBarCubeFilters,
  dashboardSummary?: boolean,
  includeSectionDimension?: boolean,
  order?: Record<string, QueryOrder>,
): Query {
  // Include Account dimensions if filtering by account or if filters contain account-related filters
  const includeAccountDimensions = hasAccountFilters(filters);

  return {
    measures: [
      `${CUBE_NAME}.monthlyListSavingOpportunity`,
      `${CUBE_NAME}.monthlyBilledSavingOpportunity`,
      `${CUBE_NAME}.monthlyEffectiveSavingOpportunity`,
      `${CUBE_NAME}.nonCompliantResourceCount`,
    ],
    dimensions: [
      `${CUBE_NAME}.dashboard_key`,
      ...(sectionKey || includeSectionDimension
        ? [`${CUBE_NAME}.section_key`]
        : []),
      ...(includeAccountDimensions
        ? ["Account.key", "Account.name", "Account.email", "Account.groups"]
        : []),
    ],
    segments: dashboardSummary && !sectionKey ? [`${CUBE_NAME}.summary`] : [],
    timeDimensions: [
      {
        dimension: `${CUBE_NAME}.date`,
        granularity: "day",
        dateRange,
      },
    ],
    filters: [
      {
        member: `${CUBE_NAME}.dashboard_key`,
        operator: "equals" as const,
        values: [dashboardKey],
      },
      ...(sectionKey
        ? [
            {
              member: `${CUBE_NAME}.section_key`,
              operator: "equals" as const,
              values: [sectionKey],
            } as Filter,
          ]
        : []),
      ...(filters ?? []),
    ],
    order: order ?? {
      [`${CUBE_NAME}.date`]: "desc",
    },
  };
}

export function useCostSavingOpportunity({
  dashboardKey,
  dateRange,
  sectionKey,
  filters,
  dashboardSummary = true,
  includeSectionDimension = false,
  order,
  enabled = true,
}: UseCostSavingOpportunityProps): UseCostSavingOpportunityResult {
  const { validatedRawData, isLoading, error, validationError } =
    useValidatedCubeQuery(
      buildQuery(
        dashboardKey,
        dateRange,
        sectionKey,
        filters,
        dashboardSummary,
        includeSectionDimension,
        order,
      ),
      costSavingOpportunityResultArraySchema,
      { skip: !enabled },
    );

  const combinedError = createCombinedError(
    { error, validationError },
    { errorPrefix: "Cost saving opportunity query validation failed" },
  );

  return {
    data: validatedRawData ?? null,
    isLoading,
    error: combinedError,
  };
}
